// src/app/navigation/admin-sidebar/admin-sidebar.component.ts
import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, inject } from '@angular/core';
import { TasksGateService } from 'src/app/modules/tasks/gate/tasks-gate.service';
import { Router, RouterModule } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AuthService } from 'src/app/modules/auth/services/auth/auth.service';
import { SubscriptionService } from 'src/app/modules/feature-toggle/services/subscription.service';
import { FeatureToggleService } from 'src/app/modules/feature-toggle/services/feature-toggle.service';
import { UserSettingsService } from 'src/app/modules/settings/user-settings/services/user-settings.service';
import { Feature } from 'src/app/modules/feature-toggle/enums/features.enum';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { LayoutMode } from 'src/app/core/services/const';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { CommonModule } from '@angular/common';
import { FeatureToggleModule } from 'src/app/modules/feature-toggle/feature-toggle.module';
import { CompanySwitcherComponent } from 'src/app/core/component/company-switcher/company-switcher.component';
import { PlatformService } from 'src/app/core/services/platform/platform.service';
import { UserPermissionsService } from 'src/app/modules/employee/services/user-permissions.service';
// Aufgaben-Feature (A5): 3-Zustands-Gate + Nav-Item + Badge-Zähler (06-ENTRYPOINT-NAV §1/§3).
import { XdsBadgeComponent } from 'src/app/shared/ui/badge/badge.component';
import { XdsIconButtonComponent } from 'src/app/shared/ui/icon-button/icon-button.component';
import { XdsTooltipDirective } from 'src/app/shared/ui/tooltip/tooltip.directive';
import { XdsNavItem } from 'src/app/shared/ui/nav-list/nav-list.component';
import { TasksGateState } from 'src/app/modules/tasks/gate';
import { buildTasksNavItem, TasksNavBadgeService } from 'src/app/modules/tasks/nav';
import { CompanySettingsService } from 'src/app/modules/settings/services/company-settings.service';

@Component({
    selector: 'app-admin-sidebar',
    templateUrl: './admin-sidebar.component.html',
    styleUrls: ['./admin-sidebar.component.css'],
    imports: [
        CommonModule,
        RouterModule,
        TranslateModule,
        FeatureToggleModule,
        CompanySwitcherComponent,
        XdsBadgeComponent,
        XdsIconButtonComponent,
        XdsTooltipDirective
    ]
})
export class AdminSidebarComponent implements OnInit, OnDestroy {
  /** Aufgaben-Gate: Tarif + Leserecht + Firmen-Schalter (Aufgaben-Einstellungen). */
  private readonly tasksGate = inject(TasksGateService);

  @Input() isCompact: boolean = false;
  @Input() isMobileMode: boolean = false;
  @Input() isOpenOnMobile: boolean = false;
  @Output() mobileClose = new EventEmitter<void>();

  // Feature Enum im Template verfügbar machen
  Feature = Feature;

  // Feature Visibility: 'active' | 'locked' | 'hidden'
  // active = Feature im Plan + Setting aktiv -> normaler Link
  // locked = Feature NICHT im Plan -> gedimmt + Lock-Icon + Plan-Badge
  // hidden = Feature im Plan aber Setting deaktiviert -> ausgeblendet
  shiftPlanningVisibility$: Observable<'active' | 'locked' | 'hidden'>;
  availabilityVisibility$: Observable<'active' | 'locked' | 'hidden'>;
  timeTrackingVisibility$: Observable<'active' | 'locked' | 'hidden'>;
  resourcePlanVisibility$: Observable<'active' | 'locked' | 'hidden'>;
  absenceVisibility$: Observable<'active' | 'locked' | 'hidden'>;
  showOrganization$: Observable<boolean>;

  /**
   * Aufgaben-Eintrag (A5): das fertige `XdsNavItem` bei Zustand „aktiv" bzw. „locked",
   * sonst `null` (versteckt → gar nicht rendern). 3-Zustands-Gate = feature.tasks
   * (Id 64) + mind. ein Task-View-Recht (06-ENTRYPOINT-NAV §3, DEC-09).
   */
  tasksNavItem$: Observable<XdsNavItem | null>;

  /**
   * Der Gate-Zustand der Aufgaben als eigener Strom — für den `locked`-Zweig im Template.
   *
   * `tasksNavItem$` allein reicht dafür nicht: `buildTasksNavItem` liefert für `active` und `locked`
   * dasselbe Item ohne Unterscheidung. Bis 08/2026 war `locked` deshalb doppelt tot — keine
   * Aufrufstelle setzte `upsellEnabled`, und es gab keine Darstellung dafür. Jetzt: hier `true`
   * (nur hier — die mobilen Tabs haben die Fläche nicht und iOS darf ohnehin nicht werben) und im
   * Template derselbe `app-locked-feature`-Zweig wie bei Planung, Verfügbarkeit, Zeiterfassung und
   * Abwesenheit.
   */
  tasksVisibility$: Observable<TasksGateState>;

  /**
   * Hier standen bis zum 14.09.2026 `swapApprovalVisibility$` und `swapApprovalCount$` für den
   * Eintrag „Ausstehende Tauschgenehmigungen". Beide sind mit dem Eintrag und seiner Route
   * entfallen — die Liste gibt es jetzt nur noch als Pop-up im Planer, wo der Manager den
   * Dienstplan vor sich hat. Gate und Zähler leben unverändert weiter (`swap-shift/gate.ts`,
   * `ShiftSwapApprovalService.pendingCount$`), nur wertet sie jetzt der Planer aus.
   */

  private destroy$ = new Subject<void>();

  constructor(
    public authService: AuthService,
    public router: Router,
    private translate: TranslateService,
    public subscriptionService: SubscriptionService,
    public featureToggleService: FeatureToggleService,
    private userSettingsService: UserSettingsService,
    private breakpointObserver: BreakpointObserver,
    private dialog: MatDialog,
    private toastr: ToastrService,
    private platformService: PlatformService,
    public userPermissions: UserPermissionsService,
    private tasksNavBadge: TasksNavBadgeService,
    private companySettingsService: CompanySettingsService
  ) {
    // Drei-Zustand-Logik: active (Link) | locked (gedimmt) | hidden (ausgeblendet)
    this.shiftPlanningVisibility$ = this.getFeatureVisibility$(
      Feature.SHIFT_PLANNING_BASIC,
      this.featureToggleService.isFeatureAvailable(Feature.SHIFT_PLANNING_BASIC)
    );
    this.availabilityVisibility$ = this.getFeatureVisibility$(
      Feature.SHIFT_SWAP_ADVANCED,
      this.featureToggleService.isFeatureAvailable(Feature.SHIFT_SWAP_ADVANCED)
    );
    this.timeTrackingVisibility$ = this.getFeatureVisibility$(
      Feature.TIME_TRACKING_BASIC,
      this.featureToggleService.isFeatureAvailable(Feature.TIME_TRACKING_BASIC)
    );
    this.resourcePlanVisibility$ = this.getFeatureVisibility$(
      Feature.SHIFT_PLANNING_BASIC,
      this.featureToggleService.isFeatureAvailable(Feature.SHIFT_PLANNING_BASIC)
    );
    this.absenceVisibility$ = this.getFeatureVisibility$(
      Feature.ABSENCE_MANAGEMENT_BASIC,
      this.featureToggleService.isFeatureAvailable(Feature.ABSENCE_MANAGEMENT_BASIC)
    );

    // Organization benötigt nur Admin-Rechte, kein spezielles Feature
    this.showOrganization$ = this.authService.currentUser$.pipe(
      map(() => this.authService.isManegeOrTeamLeader)
    );

    // Aufgaben (A5): 3-Zustands-Gate (feature.tasks + Task-View-Recht) → XdsNavItem
    // oder null (versteckt). Badge-Zahl aus TasksNavBadgeService (offene/überfällige).
    //
    // `upsellEnabled: true` seit 08/2026 — der Tarif entscheidet jetzt über die Aufgaben, und ein
    // Spark-Kunde soll erfahren, dass es sie gibt, statt einen leeren Platz in der Navigation zu
    // sehen. Nur hier gesetzt: die mobilen Tabs haben die Fläche nicht, und die Darstellung
    // (`app-locked-feature`) behandelt die Apple-Regel selbst (Hinweis statt Kauf-Link).
    const tasksGate$ = this.tasksGate.state$('manager', true);

    this.tasksVisibility$ = tasksGate$;

    this.tasksNavItem$ = combineLatest([tasksGate$, this.tasksNavBadge.counts$]).pipe(
      // Nur der aktive Zustand bekommt ein Nav-Item mit Badge; `locked` rendert das Template über
      // `tasksVisibility$` als gesperrten Eintrag, `hidden` gar nicht.
      map(([gate, counts]) => (gate === 'active' ? buildTasksNavItem(gate, counts) : null))
    );
  }

  /**
   * Drei-Zustand-Logik fuer Sidebar-Items:
   * - Feature im Plan UND Settings/Permissions aktiv -> 'active'
   * - Feature NICHT im Plan -> 'locked' (sichtbar aber gesperrt)
   * - Feature im Plan ABER Settings deaktiviert -> 'hidden' (Admin hat bewusst deaktiviert)
   */
  private getFeatureVisibility$(
    feature: Feature,
    featureAvailable$: Observable<boolean>
  ): Observable<'active' | 'locked' | 'hidden'> {
    return combineLatest([
      this.subscriptionService.hasFeature$(feature),
      featureAvailable$
    ]).pipe(
      map(([hasFeatureInPlan, isFullyAvailable]) => {
        if (!hasFeatureInPlan) return 'locked' as const;
        if (!isFullyAvailable) return 'hidden' as const;
        return 'active' as const;
      })
    );
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe([Breakpoints.XSmall, Breakpoints.Small])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        if (this.isMobileMode) {
          this.isCompact = false;
        }
      });

    this.userSettingsService.currentUserSettings$.pipe(
      takeUntil(this.destroy$)
    ).subscribe((settings) => {
      if (settings && !this.isMobileMode) {
        this.isCompact = settings.ActiveLayout === LayoutMode.COMPACT_SIDEBAR;
      } else if (this.isMobileMode) {
        this.isCompact = false;
      }
    });

    // Aufgaben-Badge nur laden, wenn der Entry-Point aktiv ist (Dark-Launch: sonst
    // kein Fetch). Zähler kommen aus GET api/Tasks/my (offene/überfällige).
    this.tasksGate
      .state$('manager')
      .pipe(takeUntil(this.destroy$))
      .subscribe((gate) => {
        if (gate === 'active') {
          this.tasksNavBadge.refresh();
        } else {
          this.tasksNavBadge.reset();
        }
      });

    // Initial layout from snapshot
    const currentSettings = this.userSettingsService.getCurrentUserSettingsSnapshot();
    if (!this.isMobileMode && currentSettings) {
      this.isCompact = currentSettings.ActiveLayout === LayoutMode.COMPACT_SIDEBAR;
    } else if (this.isMobileMode) {
      this.isCompact = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  toggleCompact(): void {
    if (this.isMobileMode) return;

    const currentSettings = this.userSettingsService.getCurrentUserSettingsSnapshot();
    if (!currentSettings) return;

    const currentLayout = currentSettings.ActiveLayout;
    if (currentLayout === LayoutMode.COMPACT_SIDEBAR) {
      currentSettings.ActiveLayout = LayoutMode.SIDEBAR;
    } else if (currentLayout === LayoutMode.SIDEBAR) {
      currentSettings.ActiveLayout = LayoutMode.COMPACT_SIDEBAR;
    }
    this.userSettingsService.updateUserSettings(currentSettings).subscribe();
  }

  public closeMobileSidebar(): void {
    if (this.isMobileMode) {
      this.mobileClose.emit();
    }
  }

  private handleMobileCloseAfterNavigation(): void {
    // Close all open dialogs when navigating (fixes popup staying open issue)
    this.dialog.closeAll();

    if (this.isMobileMode && this.isOpenOnMobile) {
      this.closeMobileSidebar();
    }
  }

  /**
   * Zeigt Upgrade-Dialog für gesperrte Features
   */
  showUpgradeDialog(requiredFeature: Feature): void {
    // On iOS: Show toast directing to web app instead of navigating to subscription page
    if (this.platformService.isIos()) {
      this.toastr.info(
        this.translate.instant('FEATURES.SUBSCRIPTION_VIA_WEB'),
        this.translate.instant('FEATURES.UPGRADE_REQUIRED'),
        {
          timeOut: 5000,
          enableHtml: true,
          tapToDismiss: true,
          positionClass: 'toast-top-center',
        }
      );
      return;
    }

    const featureName = this.translate.instant(
      `FEATURES.${Feature[requiredFeature]}`
    );

    this.toastr.info(
      this.translate.instant('FEATURES.UPGRADE_REQUIRED_FOR', {
        feature: featureName,
      }),
      this.translate.instant('FEATURES.UPGRADE_REQUIRED'),
      {
        timeOut: 5000,
        enableHtml: true,
        tapToDismiss: true,
        positionClass: 'toast-top-center',
      }
    );

    // Navigate to subscription settings after short delay
    setTimeout(() => {
      this.router.navigate(['/settings/subscription']);
      this.handleMobileCloseAfterNavigation();
    }, 1000);
  }

  // Navigation methods - nur Mobile-Sidebar schließen, Navigation durch routerLink
  onDashboardClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onEmployeeDashboardClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onEmployeeClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onTimeRecordingClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onAbsenceClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onPlannerClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onTasksClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onAvailabilityClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onResourcePlanClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onSettingsClick(): void {
    this.handleMobileCloseAfterNavigation();
  }

  onDashboardClickImage(): void {
    if (this.authService.isManegeOrTeamLeader) {
      this.router.navigateByUrl('/dashboard');
    } else {
      this.router.navigateByUrl('/employeedashboard');
    }
    this.handleMobileCloseAfterNavigation();
  }

  onOrganizationClick(): void {
    this.handleMobileCloseAfterNavigation();
  }
}
